import React , { useState} from 'react';
import '../styles/ObjectItem.css'
import logo_nophoto from '../assets/nophoto.png';
import Reserve from '../components/Reserve';
import { isDemoKey } from "../services/kdoServices";
import { EditableObject } from '../bean/EditableObject';
import { useNotification } from '../components/NotificationProvider';

const UNDO_DELETE_DELAY_MS = 5000;
const ICON_COLORS = ['#DBEAFE','#DCFCE7','#FEF9C3','#FCE7F3','#F3E8FF'];
const getBgColor = (id) => ICON_COLORS[(parseInt(id, 10) || 0) % ICON_COLORS.length];

const ObjectItem = ({ p_key, p_object, p_tabObject, p_setTabObject, p_isProprio, p_user, p_setLightboxImg , p_onEditObject, p_onDetailObject, p_onReserved}) => {
    const [editableObj, setEditableObj] = useState(new EditableObject())
    const [imageError, setImageError] = useState(false);
    const { addNotification } = useNotification();

    function supprimeObjet(p_id) {
        const isDemo = isDemoKey(p_key);
        if (isDemo) {
            alert("Mode démonstration, l'objet ne sera supprimé que dans cette session de démo, il reviendra si vous rechargez de la page.");
        }

        const objectIndex = p_tabObject.findIndex(item => item.id === p_id);
        if (objectIndex === -1) {
            addNotification("Objet introuvable pour la suppression.", "error");
            return;
        }

        const removedObject = p_tabObject[objectIndex];
        p_setTabObject(prev => prev.filter(item => item.id !== p_id));

        const restoreObject = () => {
            p_setTabObject(prev => {
                if (prev.some(item => item.id === removedObject.id)) {
                    return prev;
                }
                const updated = [...prev];
                const insertIndex = Math.min(objectIndex, updated.length);
                updated.splice(insertIndex, 0, removedObject);
                return updated;
            });
        };

        let isCancelled = false;
        const finalizeDeletion = async () => {
            if (isCancelled) {
                return;
            }
            if (isDemo) {
                return;
            }
            const success = await deleteObject(p_id);
            if (!success && !isCancelled) {
                restoreObject();
            }
        };

        const timerId = window.setTimeout(finalizeDeletion, UNDO_DELETE_DELAY_MS);

        addNotification(
            isDemo ? "Suppression en mode démo. Annuler ?" : "Objet supprimé. Annuler ?",
            "warning",
            UNDO_DELETE_DELAY_MS,
            {
                label: "Annuler",
                onAction: () => {
                    if (isCancelled) {
                        return;
                    }
                    isCancelled = true;
                    window.clearTimeout(timerId);
                    restoreObject();
                    addNotification("Suppression annulée.", "info", 2500);
                }
            }
        );
    }

    async function deleteObject(p_id) {
        const requestOptions = {
            method: "POST",
            mode: 'cors',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                id: p_id
            }),
        };

        try {
            const response = await fetch("https://app.listaki.fr/api/WS/deleteObject.php?key=" + p_key, requestOptions);
            if (response.ok) {
                const v_responseJson = await response.json();
                if (v_responseJson['success'] === true) {
                    return true;
                }
                const apiMessage = v_responseJson?.message || "Suppression impossible.";
                addNotification(apiMessage, "error");
                return false;
            } else {
                addNotification("Erreur lors de l'appel à l'API.", "error");
                return false;
            }
        } catch (err) {
            addNotification("Erreur pendant la suppression de l'objet.", "error");
            return false;
        }
    }

    function editObject(p_id) {
            p_onEditObject(p_id);
            setEditableObj(EditableObject.fromDBObject(p_object));
    }

    function detailObject(p_id) {
            p_onDetailObject(p_id);
            setEditableObj(EditableObject.fromDBObject(p_object));
    }

    const v_title = p_object.objectname && p_object.objectname.length > 50
        ? p_object.objectname.substring(0, 50) + "..."
        : p_object.objectname || '';
    const v_link = p_object.affiliateLink != null ? p_object.affiliateLink : p_object.tinyCode != null
        ? 'https://fliz.ly/' + p_object.tinyCode + '_w'
        : p_object.urllink;

    const isReserved = Boolean(p_object.reservationid);

    return (
        <div key={p_object.id} className={`kdo-object${isReserved ? ' kdo-object--reserved' : ''}`}>
            <div
                className="kdo-object-icon"
                style={{ background: getBgColor(p_object.id) }}
                onClick={() => p_setLightboxImg(p_object.imagelink || logo_nophoto)}
            >
                {p_object.imagelink && !imageError ? (
                    <img
                        src={p_object.imagelink}
                        alt={p_object.objectname}
                        loading="lazy"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <span className="kdo-object-emoji">🎁</span>
                )}
            </div>
            <div
                className='kdo-object-body'
                onClick={() => { if (window.umami) window.umami.track('show-object'); detailObject(p_object.id); }}
                style={{ cursor: 'pointer' }}
            >
                <div className='kdo-object-title'>
                    {v_link ? (
                        <a
                            href={v_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.stopPropagation(); if (window.umami) window.umami.track('click-product-link', { storeName: p_object.storeName }); }}
                        >{v_title}</a>
                    ) : (
                        <span>{v_title}</span>
                    )}
                </div>
                {p_object.storeName ? <div className='kdo-object-store'>{p_object.storeName}</div> : null}
                {p_object.price > 0 ? <div className='kdo-object-price'>{p_object.price} €</div> : null}
            </div>
            <div className='kdo-object-actions'>
                {p_isProprio ? (
                    <>
                        {isReserved ? (
                            <span className="kdo-object-status-badge kdo-object-status-reserved">Réservé</span>
                        ) : (
                            <span className="kdo-object-status-badge kdo-object-status-available">Disponible</span>
                        )}
                        <button className="kdo-object-action-btn kdo-object-action-btn--delete" onClick={() => { if (window.umami) window.umami.track('delete-object'); supprimeObjet(p_object.id); }} title="Supprimer">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                    </>
                ) : (
                    <Reserve
                        p_object={p_object}
                        p_key={p_key}
                        p_isProprio={p_isProprio}
                        p_withButton={true}
                        p_onReserved={(reservationId) => {
                            if (typeof p_onReserved === "function") {
                                p_onReserved(p_object.id, reservationId);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(ObjectItem);
